import { useContext, useState, useMemo } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { saveLiquidacion, deleteLiquidacionesDeLocadorMes } from "../../../../application/liquidaciones";
import { formatMonto, calcularComision } from "../../../../utils/rentalCalculations";
import { montoEnPeriodo } from "../../../../utils/montoHistorico";
import {
  FaPrint,
  FaFileDownload,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
  FaTimes,
  FaSearch,
  FaReceipt,
} from "react-icons/fa";
import MoneyInput from "../../../../components/MoneyInput";
import ComprobantePagoModal from "../../../../components/ComprobantePago/ComprobantePagoModal";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import { nombreCompleto } from "../../../../utils/nombre";
import ModalPortal from "../../../../components/ModalPortal";
import logo from "../../../../assets/img/logos/logoColor.png";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function Liquidacion() {
  const { contratosActivos, locadores, locatarios, pagos, liquidaciones, gastos, aumentosAplicados, config, refreshLiquidaciones } = useContext(AlquileresContext);
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [search, setSearch] = useState("");
  const [modalLocador, setModalLocador] = useState(null);
  const [selected, setSelected] = useState({});
  const [pagoForm, setPagoForm] = useState({ montoPagado: "", observaciones: "" });
  const [saving, setSaving] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [confirmacion, setConfirmacion] = useState(null); // { message, onConfirm, title, confirmLabel }

  const pedirConfirmacion = (message, onConfirm, opts = {}) =>
    setConfirmacion({ message, onConfirm, ...opts });

  // Group contracts by locador + add payment status from both sides
  const agrupado = useMemo(() => {
    // Respaldo de nombres por id (algunos contratos guardaron el nombre vacío).
    const nombrePorId = (lista) => {
      const m = {};
      lista.forEach((p) => { if (p.idFirebase) m[p.idFirebase] = nombreCompleto(p); });
      return m;
    };
    const locadorMap = nombrePorId(locadores);
    const locatarioMap = nombrePorId(locatarios);
    const telefonoLocadorPorId = {};
    locadores.forEach((p) => { if (p.idFirebase) telefonoLocadorPorId[p.idFirebase] = p.telefono || ""; });

    const groups = {};
    contratosActivos.forEach((rawC) => {
      const c = {
        ...rawC,
        locadorNombre: rawC.locadorNombre || locadorMap[rawC.locadorId] || "",
        locatarioNombre: rawC.locatarioNombre || locatarioMap[rawC.locatarioId] || "",
      };
      const key = c.locadorId || c.locadorNombre;
      if (!groups[key]) {
        groups[key] = {
          groupKey: key,
          locadorId: c.locadorId,
          locadorNombre: c.locadorNombre,
          telefono: telefonoLocadorPorId[c.locadorId] || "",
          contratos: [],
          totalARS: 0, totalUSD: 0,
          comisionARS: 0, comisionUSD: 0,
          pagarARS: 0, pagarUSD: 0,
          cobradoARS: 0, cobradoUSD: 0,
        };
      }
      // Check if tenant paid this contract this month
      const pago = pagos.find(
        (p) => p.contratoId === c.idFirebase && p.mes === mes && p.anio === anio
      );
      const cobrado = pago ? Number(pago.montoPagado) || 0 : 0;

      // Monto vigente en el mes liquidado: si el contrato aumento despues, la
      // liquidacion vieja tiene que seguir mostrando el monto de entonces.
      const { monto, esHistorico } = montoEnPeriodo(c, anio, mes, { aumentosAplicados, pago });
      const comisionPct = Number(c.comisionPorcentaje) || config?.comisionPorcentaje || 5;
      const comision = esHistorico
        ? calcularComision(monto, comisionPct)
        : Number(c.comisionMonto) || 0;
      const aPagar = monto - comision;

      groups[key].contratos.push({
        ...c,
        montoActual: monto,
        comisionMonto: comision,
        montoEsHistorico: esHistorico,
        pagoInquilino: pago,
        cobrado,
        aPagar,
      });

      if (c.moneda === "USD") {
        groups[key].totalUSD += monto;
        groups[key].comisionUSD += comision;
        groups[key].pagarUSD += aPagar;
        groups[key].cobradoUSD += cobrado;
      } else {
        groups[key].totalARS += monto;
        groups[key].comisionARS += comision;
        groups[key].pagarARS += aPagar;
        groups[key].cobradoARS += cobrado;
      }
    });

    // Add gastos and liquidacion status
    return Object.values(groups)
      .map((g) => {
        // Cruce de gastos/liquidaciones con el grupo. Si ambos lados tienen
        // locadorId manda el id: cruzar también por nombre hacía que dos fichas
        // homónimas (mismo nombre, ids distintos) se robaran los registros una a
        // la otra. El nombre queda solo como respaldo para los registros viejos
        // que se guardaron sin locadorId.
        const mismoLocador = (r) =>
          r.locadorId && g.locadorId
            ? r.locadorId === g.locadorId
            : r.locadorNombre === g.locadorNombre;

        // Gastos del propietario este mes (solo los que paga el propietario)
        const gastosLocador = gastos.filter(
          (gasto) =>
            gasto.mes === mes && gasto.anio === anio &&
            gasto.pagadoPor === "PROPIETARIO" &&
            mismoLocador(gasto)
        );
        const totalGastos = gastosLocador.reduce((s, gasto) => s + (Number(gasto.monto) || 0), 0);

        // Neto = a pagar - gastos
        const netoARS = g.pagarARS - (g.totalUSD > 0 ? 0 : totalGastos);
        const netoUSD = g.pagarUSD;
        const neto = netoARS + netoUSD;

        const liq = liquidaciones.find(
          (l) => mismoLocador(l) && l.mes === mes && l.anio === anio
        );
        // Which contracts have already been settled with the owner.
        // Legacy liquidaciones (sin contratosPagados) se asumen pagadas por completo.
        const paidIds = liq
          ? (Array.isArray(liq.contratosPagados) ? liq.contratosPagados : g.contratos.map((c) => c.idFirebase))
          : [];
        const contratos = g.contratos.map((c) => ({ ...c, pagadoAlDueno: paidIds.includes(c.idFirebase) }));

        const montoPagado = liq ? Number(liq.montoPagado) || 0 : 0;
        const allPaid = contratos.length > 0 && contratos.every((c) => c.pagadoAlDueno);
        const estado = !liq ? "pendiente" : allPaid ? "pagado" : "parcial";
        const pendiente = Math.max(0, neto - montoPagado);

        return {
          ...g, contratos, gastosLocador, totalGastos,
          netoARS, netoUSD, neto, liquidacion: liq,
          paidIds, montoPagado, allPaid, estado, pendiente,
          countPagados: contratos.filter((c) => c.pagadoAlDueno).length,
        };
      })
      .filter((g) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          g.locadorNombre?.toLowerCase().includes(s) ||
          g.contratos.some((c) => c.propiedad?.toLowerCase().includes(s) || c.locatarioNombre?.toLowerCase().includes(s))
        );
      })
      .sort((a, b) => a.locadorNombre.localeCompare(b.locadorNombre));
  }, [contratosActivos, locadores, locatarios, pagos, liquidaciones, gastos, aumentosAplicados, config, mes, anio, search]);

  const totales = useMemo(() => {
    return agrupado.reduce(
      (acc, g) => ({
        totalARS: acc.totalARS + g.totalARS,
        totalUSD: acc.totalUSD + g.totalUSD,
        comisionARS: acc.comisionARS + g.comisionARS,
        comisionUSD: acc.comisionUSD + g.comisionUSD,
        gastosARS: acc.gastosARS + g.totalGastos,
        pagarARS: acc.pagarARS + g.pagarARS,
        pagarUSD: acc.pagarUSD + g.pagarUSD,
        cobradoARS: acc.cobradoARS + g.cobradoARS,
        cobradoUSD: acc.cobradoUSD + g.cobradoUSD,
        pagadoARS: acc.pagadoARS + g.montoPagado,
        pagados: acc.pagados + (g.estado === "pagado" ? 1 : 0),
        pendientes: acc.pendientes + (g.estado !== "pagado" ? 1 : 0),
      }),
      { totalARS: 0, totalUSD: 0, comisionARS: 0, comisionUSD: 0, gastosARS: 0, pagarARS: 0, pagarUSD: 0, cobradoARS: 0, cobradoUSD: 0, pagadoARS: 0, pagados: 0, pendientes: 0 }
    );
  }, [agrupado]);

  // Suma de los contratos tildados (que todavia no estan pagados al dueno)
  const recalcMonto = (group, sel) => {
    const sum = group.contratos.reduce(
      (s, c) => (!c.pagadoAlDueno && sel[c.idFirebase] ? s + c.aPagar : s),
      0
    );
    // Los gastos se descuentan recien en el primer pago al dueno.
    const gastosPend = group.montoPagado > 0 ? 0 : group.totalGastos;
    return Math.max(0, sum - gastosPend);
  };

  const openPagar = (group) => {
    const sel = {};
    group.contratos.forEach((c) => {
      // Pre-tildar los contratos cuyo inquilino ya pago y que no se pagaron al dueno.
      if (!c.pagadoAlDueno && c.cobrado > 0) sel[c.idFirebase] = true;
    });
    setModalLocador(group);
    setSelected(sel);
    setPagoForm({ montoPagado: recalcMonto(group, sel), observaciones: "" });
  };

  const toggleContrato = (id) => {
    const sel = { ...selected, [id]: !selected[id] };
    setSelected(sel);
    setPagoForm((f) => ({ ...f, montoPagado: recalcMonto(modalLocador, sel) }));
  };

  const setSeleccion = (mode) => {
    const sel = {};
    modalLocador.contratos.forEach((c) => {
      if (c.pagadoAlDueno) return;
      if (mode === "todos") sel[c.idFirebase] = true;
      else if (mode === "cobrados") sel[c.idFirebase] = c.cobrado > 0;
      else if (mode === "ninguno") sel[c.idFirebase] = false;
    });
    setSelected(sel);
    setPagoForm((f) => ({ ...f, montoPagado: recalcMonto(modalLocador, sel) }));
  };

  const ejecutarPago = async () => {
    if (!modalLocador) return;
    setSaving(true);
    try {
      const selIds = modalLocador.contratos
        .filter((c) => !c.pagadoAlDueno && selected[c.idFirebase])
        .map((c) => c.idFirebase);
      const newPaidIds = Array.from(new Set([...(modalLocador.paidIds || []), ...selIds]));
      const addMonto = Number(pagoForm.montoPagado) || 0;
      const newMonto = (modalLocador.montoPagado || 0) + addMonto;
      const fecha = new Date().toISOString().split("T")[0];
      const histPrev = modalLocador.liquidacion?.pagos || [];
      const nuevoPago = { fecha, monto: addMonto, contratoIds: selIds, observaciones: pagoForm.observaciones || "" };

      await saveLiquidacion({
        locadorId: modalLocador.locadorId,
        locadorNombre: modalLocador.locadorNombre,
        mes,
        anio,
        montoPagado: newMonto,
        montoEsperado: modalLocador.neto,
        contratosPagados: newPaidIds,
        pagos: [...histPrev, nuevoPago],
        fechaPago: fecha,
        observaciones: pagoForm.observaciones || "",
      });
      await refreshLiquidaciones();
      setModalLocador(null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handlePagar = () => {
    if (!modalLocador) return;
    // Avisar si se va a pagar al dueño contratos cuyo inquilino todavía no pagó.
    const impagos = modalLocador.contratos.filter(
      (c) => !c.pagadoAlDueno && selected[c.idFirebase] && !c.cobrado
    );
    if (impagos.length > 0) {
      pedirConfirmacion(
        `Estás por pagarle al propietario ${impagos.length} contrato${impagos.length > 1 ? "s" : ""} cuyo inquilino todavía no pagó. ¿Querés continuar igual?`,
        ejecutarPago,
        { title: "El inquilino no pagó", confirmLabel: "Pagar igual" }
      );
      return;
    }
    ejecutarPago();
  };

  const handleAnular = (group) => {
    pedirConfirmacion(
      "¿Anular TODOS los pagos al propietario de este mes? Se borra el registro completo.",
      async () => {
        try {
          await deleteLiquidacionesDeLocadorMes(group.locadorId, mes, anio);
          await refreshLiquidaciones();
        } catch (error) {
          console.error("Error:", error);
        }
      },
      { title: "Anular pago", confirmLabel: "Anular" }
    );
  };

  // Anula el pago al dueño de UN contrato puntual (sin tocar el resto).
  const handleAnularContrato = (group, c) => {
    const liq = group.liquidacion;
    if (!liq) return;
    pedirConfirmacion(
      `¿Anular el pago al dueño de "${c.propiedad}"${c.locatarioNombre ? ` (${c.locatarioNombre})` : ""}?`,
      async () => {
        try {
          const newPaidIds = (Array.isArray(liq.contratosPagados) ? liq.contratosPagados : group.paidIds || [])
            .filter((id) => id !== c.idFirebase);
          // Si no queda ningún contrato pagado, se borra el registro completo.
          if (newPaidIds.length === 0) {
            await deleteLiquidacionesDeLocadorMes(group.locadorId, mes, anio);
            await refreshLiquidaciones();
            return;
          }
          const newMonto = Math.max(0, (Number(liq.montoPagado) || 0) - (Number(c.aPagar) || 0));
          const nuevosPagos = (liq.pagos || []).map((p) => ({
            ...p,
            contratoIds: (p.contratoIds || []).filter((id) => id !== c.idFirebase),
          }));
          await saveLiquidacion({
            locadorId: group.locadorId,
            locadorNombre: group.locadorNombre,
            mes,
            anio,
            montoPagado: newMonto,
            montoEsperado: group.neto,
            contratosPagados: newPaidIds,
            pagos: nuevosPagos,
            fechaPago: liq.fechaPago,
            observaciones: liq.observaciones || "",
          });
          await refreshLiquidaciones();
        } catch (error) {
          console.error("Error:", error);
        }
      },
      { title: "Anular pago", confirmLabel: "Anular" }
    );
  };

  const handlePrint = () => window.print();

  // Imprime solo la ficha de un propietario (su tabla de contratos del mes).
  const handlePrintGroup = (group) => {
    const esc = (s) =>
      String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

    const estadoTexto =
      group.estado === "pagado"
        ? `Pagado · ${formatMonto(group.montoPagado)}${group.liquidacion?.fechaPago ? ` · ${group.liquidacion.fechaPago}` : ""}`
        : group.estado === "parcial"
        ? `${group.countPagados}/${group.contratos.length} contratos pagados · ${formatMonto(group.montoPagado)} de ${formatMonto(group.neto)} · falta ${formatMonto(group.pendiente)}`
        : `${group.contratos.length} contratos · neto ${formatMonto(group.neto)} · pendiente de pago`;

    const filas = group.contratos
      .map(
        (c) => `
        <tr>
          <td>${esc(c.locatarioNombre) || "—"}</td>
          <td>${esc(c.propiedad)}</td>
          <td class="num">${formatMonto(c.montoActual, c.moneda)}</td>
          <td class="num">${formatMonto(c.comisionMonto, c.moneda)}</td>
          <td class="num verde">${formatMonto(c.aPagar, c.moneda)}</td>
          <td class="centro">${c.cobrado > 0 ? `<span class="verde">Pagó ${formatMonto(c.cobrado, c.moneda)}</span>` : `<span class="rojo">Debe</span>`}</td>
          <td class="centro">${c.pagadoAlDueno ? `<span class="verde">Pagado</span>` : "—"}</td>
        </tr>`
      )
      .join("");

    const filasGastos = group.gastosLocador
      .map(
        (g) => `
        <tr class="gasto">
          <td colspan="4" class="num rojo">${esc(g.descripcion)} (${esc(g.categoria)})</td>
          <td class="num rojo">-${formatMonto(g.monto)}</td>
          <td></td><td></td>
        </tr>`
      )
      .join("");

    const html = `
      <html>
        <head>
          <title>Pagos al Propietario - ${esc(group.locadorNombre)} - ${MESES[mes - 1]} ${anio}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; margin: 28px; font-size: 12px; }
            .cabecera { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #e5e7eb; padding-bottom: 12px; }
            .cabecera img { height: 44px; }
            .cabecera .titulo { text-align: right; }
            .cabecera .titulo h1 { margin: 0; font-size: 16px; }
            .cabecera .titulo p { margin: 2px 0 0; color: #6b7280; font-size: 12px; }
            .locador { margin: 14px 0 10px; }
            .locador h2 { margin: 0; font-size: 15px; }
            .locador p { margin: 3px 0 0; color: #6b7280; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; color: #6b7280; border-bottom: 1px solid #d1d5db; padding: 6px 8px; }
            td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; }
            th.num, td.num { text-align: right; }
            th.centro, td.centro { text-align: center; }
            .verde { color: #16a34a; }
            .rojo { color: #dc2626; }
            tfoot td { border-bottom: none; border-top: 1px solid #d1d5db; font-weight: bold; padding-top: 8px; }
            tr.gasto td { border-top: none; font-weight: normal; font-size: 11px; padding-top: 3px; padding-bottom: 3px; }
            tr.neto td { border-top: 1px solid #d1d5db; font-size: 13px; }
            .pie { margin-top: 24px; color: #9ca3af; font-size: 10px; text-align: right; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <div class="cabecera">
            <img src="${logo}" alt="Hugo A. Plorutti y Cía" />
            <div class="titulo">
              <h1>Pagos al Propietario</h1>
              <p>${MESES[mes - 1]} ${anio}</p>
            </div>
          </div>
          <div class="locador">
            <h2>${esc(group.locadorNombre)}</h2>
            <p>${esc(estadoTexto)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Locatario</th>
                <th>Propiedad</th>
                <th class="num">Monto</th>
                <th class="num">Comisión</th>
                <th class="num">A Pagar</th>
                <th class="centro">Inquilino</th>
                <th class="centro">Al dueño</th>
              </tr>
            </thead>
            <tbody>${filas}</tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="num">Subtotal</td>
                <td class="num">${group.totalARS > 0 ? formatMonto(group.totalARS) : ""}${group.totalUSD > 0 ? ` ${formatMonto(group.totalUSD, "USD")}` : ""}</td>
                <td class="num">${group.comisionARS > 0 ? formatMonto(group.comisionARS) : ""}${group.comisionUSD > 0 ? ` ${formatMonto(group.comisionUSD, "USD")}` : ""}</td>
                <td class="num verde">${group.pagarARS > 0 ? formatMonto(group.pagarARS) : ""}${group.pagarUSD > 0 ? ` ${formatMonto(group.pagarUSD, "USD")}` : ""}</td>
                <td class="centro verde">${group.cobradoARS > 0 || group.cobradoUSD > 0 ? `${group.cobradoARS > 0 ? formatMonto(group.cobradoARS) : ""}${group.cobradoUSD > 0 ? ` ${formatMonto(group.cobradoUSD, "USD")}` : ""}` : ""}</td>
                <td></td>
              </tr>
              ${filasGastos}
              <tr class="neto">
                <td colspan="4" class="num">NETO A PAGAR</td>
                <td class="num verde">${formatMonto(group.netoARS + group.netoUSD)}</td>
                <td></td><td></td>
              </tr>
            </tfoot>
          </table>
          <div class="pie">Impreso el ${new Date().toLocaleDateString("es-AR")} · Hugo A. Plorutti y Cía</div>
        </body>
      </html>`;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const imprimir = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      // Sin afterprint confiable en todos los navegadores: se limpia después.
      setTimeout(() => iframe.remove(), 60000);
    };
    const img = doc.querySelector("img");
    if (img && !img.complete) {
      img.onload = imprimir;
      img.onerror = imprimir;
    } else {
      setTimeout(imprimir, 100);
    }
  };

  const handleExportCSV = () => {
    let csv = "Locador,Locatario,Propiedad,Moneda,Monto,Comision,A Pagar,Inquilino Pago,Pagado al Dueno\n";
    agrupado.forEach((g) => {
      g.contratos.forEach((c) => {
        csv += `"${c.locadorNombre}","${c.locatarioNombre}","${c.propiedad}","${c.moneda}",${c.montoActual},${c.comisionMonto},${c.aPagar},${c.cobrado > 0 ? "SI" : "NO"},${c.pagadoAlDueno ? "SI" : "NO"}\n`;
      });
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `liquidacion_${anio}_${String(mes).padStart(2, "0")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Datos en vivo de la seleccion del modal
  const selectedCount = modalLocador
    ? modalLocador.contratos.filter((c) => !c.pagadoAlDueno && selected[c.idFirebase]).length
    : 0;
  const selectedSum = modalLocador
    ? modalLocador.contratos.reduce((s, c) => (!c.pagadoAlDueno && selected[c.idFirebase] ? s + c.aPagar : s), 0)
    : 0;
  const gastosPendModal = modalLocador && modalLocador.montoPagado === 0 ? modalLocador.totalGastos : 0;

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Pagos al Propietario
        </h1>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <button onClick={handlePrint} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <FaPrint className="w-4 h-4" /> Imprimir
          </button>
          <button onClick={handleExportCSV} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg transition-colors">
            <FaFileDownload className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {/* Mes/Anio + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="grid grid-cols-2 sm:flex gap-2">
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100">
            {MESES.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
          </select>
          <select value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100">
            {[2024, 2025, 2026, 2027].map((a) => (<option key={a} value={a}>{a}</option>))}
          </select>
        </div>
        <div className="relative flex-1 sm:max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar locador, locatario o propiedad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Neto a Pagar</p>
          <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{formatMonto(totales.pagarARS - totales.gastosARS)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Cobrado a inquilinos</p>
          <p className="text-lg font-bold text-blue-500">{formatMonto(totales.cobradoARS)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Pagado a dueños</p>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatMonto(totales.pagadoARS)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Falta pagar</p>
          <p className="text-lg font-bold text-red-500">{formatMonto(Math.max(0, (totales.pagarARS - totales.gastosARS) - totales.pagadoARS))}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Liquidados</p>
          <p className="text-2xl font-bold text-green-500">{totales.pagados}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Pendientes</p>
          <p className="text-2xl font-bold text-red-400">{totales.pendientes}</p>
        </div>
      </div>

      {/* Grouped by locador */}
      <div className="space-y-4">
        {agrupado.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center text-gray-400">
            {search ? "No se encontraron resultados" : "No hay contratos activos para liquidar"}
          </div>
        ) : (
          agrupado.map((group) => {
            const isPagado = group.estado === "pagado";
            const isParcial = group.estado === "parcial";
            return (
              <div key={group.groupKey || group.locadorNombre} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden ${isPagado ? "ring-1 ring-green-200 dark:ring-green-900/50" : isParcial ? "ring-1 ring-amber-200 dark:ring-amber-900/50" : ""}`}>
                {/* Locador header */}
                <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {isPagado ? (
                      <FaCheckCircle className="text-green-500 w-5 h-5 flex-shrink-0" />
                    ) : isParcial ? (
                      <FaMoneyBillWave className="text-amber-500 w-5 h-5 flex-shrink-0" />
                    ) : (
                      <FaTimesCircle className="text-red-400 w-5 h-5 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                        {group.locadorNombre}
                      </h3>
                      {isPagado && (
                        <p className="text-xs text-green-600 dark:text-green-400">
                          Pagado · {formatMonto(group.montoPagado)}
                          {group.liquidacion?.fechaPago && ` · ${group.liquidacion.fechaPago}`}
                        </p>
                      )}
                      {isParcial && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {group.countPagados}/{group.contratos.length} contratos · {formatMonto(group.montoPagado)} de {formatMonto(group.neto)} · falta {formatMonto(group.pendiente)}
                        </p>
                      )}
                      {!group.liquidacion && (
                        <p className="text-xs text-gray-400">
                          {group.contratos.length} contratos · neto {formatMonto(group.neto)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    <button
                      onClick={() => openPagar(group)}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        isPagado
                          ? "bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600"
                          : isParcial
                          ? "bg-amber-500 hover:bg-amber-600 text-white"
                          : "bg-green-500 hover:bg-green-600 text-white"
                      }`}
                    >
                      <FaMoneyBillWave className="w-3 h-3" />
                      {isPagado ? "Ver detalle" : isParcial ? "Pagar resto" : "Pagar"}
                    </button>
                    <button
                      onClick={() => handlePrintGroup(group)}
                      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Imprimir solo la ficha de este propietario"
                    >
                      <FaPrint className="w-3 h-3" /> Imprimir
                    </button>
                    {group.liquidacion && group.montoPagado > 0 && (
                      <button
                        onClick={() => setComprobante(group)}
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        title="Descargar comprobante para el propietario"
                      >
                        <FaReceipt className="w-3 h-3" /> Comprobante
                      </button>
                    )}
                    {group.liquidacion && (
                      <button
                        onClick={() => handleAnular(group)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Anular pagos"
                      >
                        <FaTimes className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Contracts table */}
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Locatario</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Propiedad</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Monto</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Comision</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">A Pagar</th>
                      <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Inquilino</th>
                      <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Al dueño</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {group.contratos.map((c) => (
                      <tr key={c.idFirebase} className={c.pagadoAlDueno ? "bg-green-100 dark:bg-green-900/25 border-l-4 border-green-500" : ""}>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{c.locatarioNombre}</td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{c.propiedad}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-800 dark:text-gray-200">
                          {formatMonto(c.montoActual, c.moneda)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">
                          {formatMonto(c.comisionMonto, c.moneda)}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-600 dark:text-green-400">
                          {formatMonto(c.aPagar, c.moneda)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {c.pagoInquilino ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
                              <FaCheckCircle className="w-3 h-3" /> {formatMonto(c.cobrado, c.moneda)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-400">
                              <FaTimesCircle className="w-3 h-3" /> Debe
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {c.pagadoAlDueno ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
                                <FaCheckCircle className="w-3 h-3" /> Pagado
                              </span>
                              <button
                                onClick={() => handleAnularContrato(group, c)}
                                className="text-gray-300 hover:text-red-500 transition-colors"
                                title="Anular el pago al dueño de este contrato"
                              >
                                <FaTimes className="w-3 h-3" />
                              </button>
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-300 dark:text-gray-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                      <td colSpan={2} className="px-4 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 text-xs">
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-gray-800 dark:text-gray-200">
                        {group.totalARS > 0 && <div>{formatMonto(group.totalARS)}</div>}
                        {group.totalUSD > 0 && <div>{formatMonto(group.totalUSD, "USD")}</div>}
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-primary">
                        {group.comisionARS > 0 && <div>{formatMonto(group.comisionARS)}</div>}
                        {group.comisionUSD > 0 && <div>{formatMonto(group.comisionUSD, "USD")}</div>}
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-green-600 dark:text-green-400">
                        {group.pagarARS > 0 && <div>{formatMonto(group.pagarARS)}</div>}
                        {group.pagarUSD > 0 && <div>{formatMonto(group.pagarUSD, "USD")}</div>}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {(group.cobradoARS > 0 || group.cobradoUSD > 0) && (
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                            {group.cobradoARS > 0 && formatMonto(group.cobradoARS)}
                            {group.cobradoUSD > 0 && ` ${formatMonto(group.cobradoUSD, "USD")}`}
                          </span>
                        )}
                      </td>
                      <td></td>
                    </tr>
                    {group.totalGastos > 0 && (
                      <>
                        {group.gastosLocador.map((g) => (
                          <tr key={g.idFirebase} className="bg-red-50/50 dark:bg-red-900/5">
                            <td colSpan={2} className="px-4 py-1.5 text-right text-xs text-red-500">
                              {g.descripcion} ({g.categoria})
                            </td>
                            <td></td>
                            <td></td>
                            <td className="px-4 py-1.5 text-right text-xs font-medium text-red-500">
                              -{formatMonto(g.monto)}
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        ))}
                        <tr className="border-t border-gray-200 dark:border-gray-700 bg-green-50/50 dark:bg-green-900/5">
                          <td colSpan={2} className="px-4 py-2 text-right font-bold text-gray-600 dark:text-gray-400 text-xs">
                            NETO A PAGAR
                          </td>
                          <td></td>
                          <td></td>
                          <td className="px-4 py-2 text-right font-bold text-green-700 dark:text-green-400 text-base">
                            {formatMonto(group.netoARS)}
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Pagar Propietario */}
      {modalLocador && (
        <ModalPortal>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  Pagar a Propietario
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {modalLocador.locadorNombre} · {MESES[mes - 1]} {anio}
                </p>
              </div>
              <button onClick={() => setModalLocador(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>

            {/* Resumen del estado */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Neto a pagar</p>
                <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{formatMonto(modalLocador.neto)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Ya pagado</p>
                <p className="text-sm font-bold text-green-600 dark:text-green-400">{formatMonto(modalLocador.montoPagado)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Falta</p>
                <p className="text-sm font-bold text-red-500">{formatMonto(modalLocador.pendiente)}</p>
              </div>
            </div>

            {/* Acciones rapidas de seleccion */}
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Seleccioná qué contratos pagar
              </label>
              <div className="flex gap-1.5 text-[11px]">
                <button onClick={() => setSeleccion("cobrados")} className="px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100">
                  Los cobrados
                </button>
                <button onClick={() => setSeleccion("todos")} className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200">
                  Todos
                </button>
                <button onClick={() => setSeleccion("ninguno")} className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200">
                  Ninguno
                </button>
              </div>
            </div>

            {/* Lista de contratos */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 mb-4 max-h-72 overflow-y-auto">
              {modalLocador.contratos.map((c) => {
                const checked = c.pagadoAlDueno || !!selected[c.idFirebase];
                return (
                  <label
                    key={c.idFirebase}
                    className={`flex items-center gap-3 px-3 py-2.5 ${
                      c.pagadoAlDueno ? "bg-green-50/50 dark:bg-green-900/10 cursor-default" : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={c.pagadoAlDueno}
                      onChange={() => toggleContrato(c.idFirebase)}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-60"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {c.locatarioNombre}
                        <span className="text-gray-400 font-normal"> · {c.propiedad}</span>
                      </p>
                      <p className="text-[11px]">
                        {c.pagadoAlDueno ? (
                          <span className="text-green-600 dark:text-green-400">Ya pagado al dueño</span>
                        ) : c.cobrado > 0 ? (
                          <span className="text-green-600 dark:text-green-400">Inquilino pagó {formatMonto(c.cobrado, c.moneda)}</span>
                        ) : (
                          <span className="text-red-400">Inquilino no pagó</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-green-600 dark:text-green-400">{formatMonto(c.aPagar, c.moneda)}</p>
                      <p className="text-[10px] text-gray-400">a pagar</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Subtotal seleccion */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4 space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Seleccionado ({selectedCount}):</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{formatMonto(selectedSum)}</span>
              </div>
              {gastosPendModal > 0 && (
                <div className="flex justify-between">
                  <span className="text-xs text-red-500">Gastos ({modalLocador.gastosLocador.length}):</span>
                  <span className="text-sm font-semibold text-red-500">-{formatMonto(gastosPendModal)}</span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monto a Pagar ahora *
                </label>
                <MoneyInput
                  value={pagoForm.montoPagado}
                  onChange={(v) => setPagoForm({ ...pagoForm, montoPagado: v })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400 mt-1">Se calcula con los contratos tildados. Podés ajustarlo a mano.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observaciones
                </label>
                <input
                  type="text"
                  value={pagoForm.observaciones}
                  onChange={(e) => setPagoForm({ ...pagoForm, observaciones: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Transferencia, efectivo, cheque..."
                />
              </div>

              {/* Historial de pagos */}
              {modalLocador.liquidacion?.pagos?.length > 0 && (
                <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Pagos realizados</p>
                  <ul className="space-y-1">
                    {modalLocador.liquidacion.pagos.map((p, i) => (
                      <li key={i} className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
                        <span>{p.fecha}{p.observaciones ? ` · ${p.observaciones}` : ""}</span>
                        <span className="font-medium">{formatMonto(p.monto)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button onClick={() => setModalLocador(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Cancelar
                </button>
                <button
                  onClick={handlePagar}
                  disabled={saving || ((Number(pagoForm.montoPagado) || 0) <= 0 && selectedCount === 0)}
                  className="px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? "Guardando..." : selectedCount > 0 ? `Pagar ${selectedCount} contrato${selectedCount > 1 ? "s" : ""}` : "Confirmar Pago"}
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {comprobante && (
        <ComprobantePagoModal
          group={comprobante}
          periodo={`${MESES[mes - 1]} ${anio}`}
          onClose={() => setComprobante(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmacion}
        message={confirmacion?.message}
        title={confirmacion?.title || "Confirmar"}
        confirmLabel={confirmacion?.confirmLabel || "Confirmar"}
        onCancel={() => setConfirmacion(null)}
        onConfirm={async () => {
          const fn = confirmacion?.onConfirm;
          setConfirmacion(null);
          if (fn) await fn();
        }}
      />
    </div>
  );
}

export default Liquidacion;
